import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, UserPlus, KeyRound, Ban, CheckCircle2, Megaphone, ArrowLeft, Copy, Lock } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, Chip } from "@/components/common";
import { useList, post, Table, PageTitle } from "@/pages/admin/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function PasswordResetDialog({ target, kind, onClose }) {
  const [mode, setMode] = useState("temp");
  const [pw, setPw] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/${kind}/${target.id}/password`, { new_password: mode === "custom" ? pw : null, notify });
      if (data.temporary_password) setResult(data.temporary_password);
      else { toast.success("Passwort zurückgesetzt"); onClose(); }
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pwreset-dialog">
        <DialogHeader><DialogTitle>Passwort zurücksetzen</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-4" data-testid="pwreset-result">
            <p className="w-muted text-sm">Neues vorläufiges Passwort für <b>{target.name}</b>. Teile es sicher mit – es wird nicht erneut angezeigt.</p>
            <div className="flex items-center gap-2">
              <code className="w-input font-mono select-all flex-1 !h-11 flex items-center">{result}</code>
              <button className="w-btn w-btn-secondary !px-3" onClick={() => { navigator.clipboard.writeText(result); toast.success("Kopiert"); }} data-testid="pwreset-copy"><Copy size={16} aria-hidden="true" /></button>
            </div>
            <button className="w-btn w-btn-primary w-full" onClick={onClose} data-testid="pwreset-done">Fertig</button>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={submit}>
            <p className="w-muted text-sm">Passwort für <b>{target.name}</b> ({target.email}) zurücksetzen.</p>
            <div className="flex gap-2">
              <button type="button" className={`w-btn flex-1 !min-h-[40px] ${mode === "temp" ? "w-btn-primary" : "w-btn-secondary"}`} onClick={() => setMode("temp")} data-testid="pwreset-mode-temp">Temporär generieren</button>
              <button type="button" className={`w-btn flex-1 !min-h-[40px] ${mode === "custom" ? "w-btn-primary" : "w-btn-secondary"}`} onClick={() => setMode("custom")} data-testid="pwreset-mode-custom">Selbst festlegen</button>
            </div>
            {mode === "custom" && (
              <label className="block"><span className="w-label">Neues Passwort (min. 8 Zeichen)</span>
                <input className="w-input" type="text" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="pwreset-input" /></label>
            )}
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="pwreset-notify" /> Person per E-Mail informieren</label>
            <button className="w-btn w-btn-primary w-full" disabled={busy} data-testid="pwreset-submit">{busy ? "..." : "Passwort zurücksetzen"}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

const PERM_LABELS = {
  providers: "Anbieter verwalten", bookings: "Buchungen & Erstattungen", complaints: "Reklamationen",
  insurance: "Versicherungsfälle", tickets: "Support-Tickets", reviews: "Bewertungen moderieren",
  finance: "Finanzen & Payouts", config: "Konfiguration & Preise", users: "Nutzer verwalten & sperren",
  messages: "Postfach & Rundmails", staff: "Mitarbeiter verwalten", audit: "Audit-Log",
};

/* ---------------- Nutzerverwaltung ---------------- */
export function Users() {
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const { data, reload } = useList(`/admin/users${role ? `?role=${role}` : ""}`);
  const [edit, setEdit] = useState(null);
  const [block, setBlock] = useState(null);
  const [pwReset, setPwReset] = useState(null);
  if (!data) return <Loader />;
  const filtered = q ? data.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase())) : data;
  return (
    <div>
      <PageTitle sub="Alle Kunden & Waschhelden · Name/E-Mail ändern, sperren oder entsperren">Nutzer</PageTitle>
      <div className="flex flex-wrap gap-3 mb-4">
        <input className="w-input max-w-xs" placeholder="Suche Name oder E-Mail" value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search" />
        <select className="w-input !h-11 max-w-[180px]" value={role} onChange={(e) => setRole(e.target.value)} data-testid="users-role-filter">
          <option value="">Alle Rollen</option><option value="customer">Kunden</option><option value="provider">Waschhelden</option>
        </select>
      </div>
      <Table testId="admin-users-table" rows={filtered} cols={[
        ["Name", (u) => <div><p className="font-semibold">{u.name}</p><p className="w-muted text-sm">{u.email}</p></div>],
        ["Rolle", (u) => <Chip tone={u.role === "provider" ? "mint" : "info"}>{u.role === "provider" ? "Waschheld" : "Kunde"}</Chip>],
        ["Status", (u) => u.blocked ? <Chip tone="error"><Ban size={14} aria-hidden="true" /> gesperrt</Chip> : <Chip tone="mint">aktiv</Chip>],
        ["Registriert", (u) => fmtDateTime(u.created_at)],
        ["Aktionen", (u) => (
          <div className="flex flex-wrap gap-2">
            <button className="w-btn w-btn-secondary !min-h-[38px] !px-4" onClick={() => setEdit({ ...u })} data-testid={`user-edit-${u.id}`}>Bearbeiten</button>
            <button className="w-btn w-btn-secondary !min-h-[38px] !px-4" onClick={() => setPwReset({ id: u.id, name: u.name, email: u.email })} data-testid={`user-pw-${u.id}`}><Lock size={15} aria-hidden="true" /> Passwort</button>
            {u.blocked
              ? <button className="w-btn w-btn-primary !min-h-[38px] !px-4" onClick={() => post(`/admin/users/${u.id}/block`, { blocked: false }, "Entsperrt", reload)} data-testid={`user-unblock-${u.id}`}><CheckCircle2 size={16} aria-hidden="true" /> Entsperren</button>
              : <button className="w-btn w-btn-danger !min-h-[38px] !px-4" onClick={() => setBlock({ ...u })} data-testid={`user-block-${u.id}`}><Ban size={16} aria-hidden="true" /> Sperren</button>}
          </div>
        )],
      ]} />

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nutzer bearbeiten</DialogTitle></DialogHeader>
          {edit && (
            <form className="space-y-4" onSubmit={async (e) => {
              e.preventDefault();
              try { await api.patch(`/admin/users/${edit.id}`, { name: edit.name, email: edit.email }); toast.success("Gespeichert"); setEdit(null); reload(); }
              catch (err) { toast.error(errMsg(err)); }
            }}>
              <label className="block"><span className="w-label">Name</span><input className="w-input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="user-edit-name" /></label>
              <label className="block"><span className="w-label">E-Mail</span><input className="w-input" type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} data-testid="user-edit-email" /></label>
              <button className="w-btn w-btn-primary w-full" data-testid="user-edit-save">Speichern</button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!block} onOpenChange={(o) => !o && setBlock(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nutzer sperren</DialogTitle></DialogHeader>
          {block && (
            <form className="space-y-4" onSubmit={async (e) => {
              e.preventDefault();
              try { await api.post(`/admin/users/${block.id}/block`, { blocked: true, reason: block.reason || "" }); toast.success("Gesperrt"); setBlock(null); reload(); }
              catch (err) { toast.error(errMsg(err)); }
            }}>
              <p className="w-muted text-sm">{block.name} kann sich weiterhin anmelden, sieht aber einen Sperrhinweis und kann nicht buchen.</p>
              <label className="block"><span className="w-label">Grund (für den Nutzer sichtbar)</span><textarea className="w-input !h-24 py-3" placeholder="z. B. Verstoß gegen die Nutzungsbedingungen" value={block.reason || ""} onChange={(e) => setBlock({ ...block, reason: e.target.value })} data-testid="user-block-reason" /></label>
              <button className="w-btn w-btn-danger w-full" data-testid="user-block-confirm"><Ban size={16} aria-hidden="true" /> Jetzt sperren</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
      {pwReset && <PasswordResetDialog target={pwReset} kind="users" onClose={() => setPwReset(null)} />}
    </div>
  );
}

/* ---------------- Mitarbeiter / Admins ---------------- */
const ROLE_OPTS = [["admin", "Admin (voller Zugriff)"], ["support", "Support"], ["finance", "Finanzen"]];

function StaffForm({ initial, allPerms, onSubmit, submitLabel }) {
  const [f, setF] = useState(initial);
  const togglePerm = (p) => setF((x) => ({ ...x, permissions: x.permissions.includes(p) ? x.permissions.filter((y) => y !== p) : [...x.permissions, p] }));
  const isAdmin = f.role === "admin";
  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); onSubmit(f); }}>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block"><span className="w-label">Name</span><input className="w-input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="staff-name" /></label>
        <label className="block"><span className="w-label">Rolle</span>
          <select className="w-input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="staff-role">{ROLE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
      </div>
      {f.email !== undefined && <label className="block"><span className="w-label">E-Mail</span><input className="w-input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="staff-email" /></label>}
      {f.password !== undefined && <div className="grid sm:grid-cols-2 gap-3">
        <label className="block"><span className="w-label">Passwort (min. 8)</span><input className="w-input" type="text" required minLength={8} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="staff-password" /></label>
        <label className="block"><span className="w-label">Backoffice-PIN (4–12 Ziffern)</span><input className="w-input" inputMode="numeric" required minLength={4} maxLength={12} value={f.pin} onChange={(e) => setF({ ...f, pin: e.target.value.replace(/\D/g, "") })} data-testid="staff-pin" /></label>
      </div>}
      <div>
        <span className="w-label">Kompetenzen / Rechte</span>
        {isAdmin ? <p className="w-muted text-sm">Admins haben automatisch alle Rechte.</p> : (
          <div className="grid sm:grid-cols-2 gap-2 mt-1">
            {allPerms.map((p) => (
              <label key={p} className={`flex items-center gap-2 rounded-xl border p-2.5 cursor-pointer ${f.permissions.includes(p) ? "border-mint bg-mint-soft" : "border-line"}`} data-testid={`staff-perm-${p}`}>
                <input type="checkbox" className="h-4 w-4 accent-[var(--w-mint-ink)]" checked={f.permissions.includes(p)} onChange={() => togglePerm(p)} />
                <span className="text-sm font-medium">{PERM_LABELS[p] || p}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      <button className="w-btn w-btn-primary w-full" data-testid="staff-submit">{submitLabel}</button>
    </form>
  );
}

export function Staff() {
  const { user, refresh } = useAuth();
  const { data, reload } = useList("/admin/staff");
  const [create, setCreate] = useState(false);
  const [edit, setEdit] = useState(null);
  const [pin, setPin] = useState(null);
  const [pwReset, setPwReset] = useState(null);
  if (!data) return <Loader />;
  const { staff, all_perms } = data;

  const doCreate = async (f) => {
    try { await api.post("/admin/staff", { ...f, permissions: f.role === "admin" ? [] : f.permissions }); toast.success("Mitarbeiter angelegt"); setCreate(false); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const doEdit = async (f) => {
    try { await api.patch(`/admin/staff/${f.id}`, { name: f.name, role: f.role, permissions: f.role === "admin" ? [] : f.permissions }); toast.success("Gespeichert"); setEdit(null); reload(); await refresh(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div>
      <PageTitle sub="Admins & Teammitglieder mit individuellen Rechten · Backoffice-PIN je Person">Mitarbeiter</PageTitle>
      <button className="w-btn w-btn-primary mb-4" onClick={() => setCreate(true)} data-testid="staff-new-btn"><UserPlus size={18} aria-hidden="true" /> Neuen Mitarbeiter anlegen</button>
      <Table testId="admin-staff-table" rows={staff} cols={[
        ["Name", (s) => <div><p className="font-semibold">{s.name} {s.is_owner && <Chip tone="mint">Eigentümer</Chip>}</p><p className="w-muted text-sm">{s.email}</p></div>],
        ["Rolle", (s) => <Chip tone={s.role === "admin" ? "mint" : "info"}>{s.role}</Chip>],
        ["Rechte", (s) => s.role === "admin" ? <span className="w-muted text-sm">alle</span> : <span className="text-sm">{s.permissions.length} Rechte</span>],
        ["PIN", (s) => s.pin_set ? <Chip tone="mint">gesetzt</Chip> : <Chip tone="warn">fehlt</Chip>],
        ["Status", (s) => s.active ? <Chip tone="mint">aktiv</Chip> : <Chip tone="neutral">inaktiv</Chip>],
        ["Aktionen", (s) => (
          <div className="flex flex-wrap gap-2">
            <button className="w-btn w-btn-secondary !min-h-[38px] !px-4" onClick={() => setEdit({ id: s.id, name: s.name, role: s.role, permissions: s.permissions })} data-testid={`staff-edit-${s.id}`}>Rechte</button>
            <button className="w-btn w-btn-secondary !min-h-[38px] !px-4" onClick={() => setPin({ id: s.id, name: s.name, pin: "" })} data-testid={`staff-pin-${s.id}`}><KeyRound size={15} aria-hidden="true" /> PIN</button>
            {s.id !== user.id && <button className="w-btn w-btn-secondary !min-h-[38px] !px-4" onClick={() => setPwReset({ id: s.id, name: s.name, email: s.email })} data-testid={`staff-pw-${s.id}`}><Lock size={15} aria-hidden="true" /> Passwort</button>}
            {!s.is_owner && s.id !== user.id && <button className="w-btn w-btn-secondary !min-h-[38px] !px-4" onClick={async () => { try { await api.patch(`/admin/staff/${s.id}`, { active: !s.active }); toast.success(s.active ? "Deaktiviert" : "Aktiviert"); reload(); } catch (err) { toast.error(errMsg(err)); } }} data-testid={`staff-toggle-${s.id}`}>{s.active ? "Deaktivieren" : "Aktivieren"}</button>}
          </div>
        )],
      ]} />

      <Dialog open={create} onOpenChange={(o) => !o && setCreate(false)}>
        <DialogContent className="max-w-xl"><DialogHeader><DialogTitle>Neuer Mitarbeiter</DialogTitle></DialogHeader>
          <StaffForm initial={{ name: "", email: "", role: "support", password: "", pin: "", permissions: [] }} allPerms={all_perms} onSubmit={doCreate} submitLabel="Mitarbeiter anlegen" />
        </DialogContent>
      </Dialog>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-xl"><DialogHeader><DialogTitle>Rechte bearbeiten</DialogTitle></DialogHeader>
          {edit && <StaffForm initial={edit} allPerms={all_perms} onSubmit={doEdit} submitLabel="Speichern" />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!pin} onOpenChange={(o) => !o && setPin(null)}>
        <DialogContent><DialogHeader><DialogTitle>Backoffice-PIN setzen</DialogTitle></DialogHeader>
          {pin && <form className="space-y-4" onSubmit={async (e) => {
            e.preventDefault();
            try { await api.post(`/admin/staff/${pin.id}/pin`, { pin: pin.pin }); toast.success("PIN gesetzt"); setPin(null); reload(); }
            catch (err) { toast.error(errMsg(err)); }
          }}>
            <p className="w-muted text-sm">Neue PIN für {pin.name}. Teile sie der Person sicher mit.</p>
            <input className="w-input tracking-[0.4em] font-mono text-center" inputMode="numeric" required minLength={4} maxLength={12} placeholder="••••••" value={pin.pin} onChange={(e) => setPin({ ...pin, pin: e.target.value.replace(/\D/g, "") })} data-testid="staff-pin-input" />
            <button className="w-btn w-btn-primary w-full" data-testid="staff-pin-save">PIN speichern</button>
          </form>}
        </DialogContent>
      </Dialog>
      {pwReset && <PasswordResetDialog target={pwReset} kind="staff" onClose={() => setPwReset(null)} />}
    </div>
  );
}

/* ---------------- Postfach (Admin) ---------------- */
export function AdminInbox() {
  const { data: threads, reload } = useList("/admin/inbox/threads");
  const [open, setOpen] = useState(null);
  const [bc, setBc] = useState(false);
  const [dm, setDm] = useState(false);
  if (!threads) return <Loader />;
  if (open) return <AdminThread tid={open} onBack={() => { setOpen(null); reload(); }} />;
  return (
    <div>
      <PageTitle sub="Nachrichten mit Kunden & Waschhelden · Einzelnachricht oder Rundmail">Postfach</PageTitle>
      <div className="flex flex-wrap gap-2 mb-4">
        <button className="w-btn w-btn-primary" onClick={() => setDm(true)} data-testid="direct-msg-btn"><Send size={18} aria-hidden="true" /> Nachricht an Nutzer</button>
        <button className="w-btn w-btn-secondary" onClick={() => setBc(true)} data-testid="broadcast-btn"><Megaphone size={18} aria-hidden="true" /> Rundnachricht senden</button>
      </div>
      <div className="space-y-2" data-testid="admin-threads">
        {!threads.length && <div className="w-card p-8 text-center w-muted">Noch keine Nachrichten.</div>}
        {threads.map((t) => (
          <button key={t.id} className="w-card p-4 w-full text-left flex items-center gap-3 w-hover-lift" onClick={() => setOpen(t.id)} data-testid={`thread-${t.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{t.subject} {t.broadcast && <Chip tone="info">Rundmail</Chip>}</p>
              <p className="w-muted text-sm truncate">{t.user_name} · {t.user_email} — {t.last_preview}</p>
            </div>
            {t.unread_staff > 0 && <span className="w-chip bg-[var(--w-error)] text-white">{t.unread_staff} neu</span>}
            <span className="w-muted text-sm whitespace-nowrap">{fmtDateTime(t.last_at)}</span>
          </button>
        ))}
      </div>
      <BroadcastDialog open={bc} onClose={() => { setBc(false); reload(); }} />
      <DirectMessageDialog open={dm} onClose={(sent) => { setDm(false); if (sent) reload(); }} />
    </div>
  );
}

function AdminThread({ tid, onBack }) {
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const load = () => api.get(`/admin/inbox/threads/${tid}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [tid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView(); }, [data]);
  const send = async (e) => {
    e.preventDefault(); if (!body.trim()) return; setBusy(true);
    try { await api.post(`/admin/inbox/threads/${tid}/messages`, { body }); setBody(""); await load(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (!data) return <Loader />;
  return (
    <div className="max-w-3xl">
      <button className="w-btn w-btn-secondary mb-4" onClick={onBack} data-testid="thread-back"><ArrowLeft size={18} aria-hidden="true" /> Zurück</button>
      <div className="w-card p-5 mb-4"><h2 className="font-bold text-lg">{data.thread.subject}</h2><p className="w-muted text-sm">{data.thread.user_name} · {data.thread.user_email}</p></div>
      <MessageList messages={data.messages} meRole="staff" endRef={endRef} />
      <form onSubmit={send} className="mt-4 flex gap-2">
        <textarea className="w-input !h-12 py-3 flex-1" placeholder="Antwort schreiben…" value={body} onChange={(e) => setBody(e.target.value)} data-testid="admin-reply-input" />
        <button className="w-btn w-btn-primary" disabled={busy} data-testid="admin-reply-send"><Send size={18} aria-hidden="true" /></button>
      </form>
    </div>
  );
}

export function MessageList({ messages, meRole, endRef }) {
  return (
    <div className="space-y-3" data-testid="message-list">
      {messages.map((m) => {
        const mine = m.sender_role === meRole;
        return (
          <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "bg-mint text-mint-on" : "bg-white border border-line"}`}>
              <p className="text-xs font-semibold opacity-70 mb-0.5">{m.sender_role === "staff" ? (mine ? "Du (Washly)" : `${m.sender_name} · Washly`) : m.sender_name}</p>
              <p className="whitespace-pre-wrap break-words">{m.body}</p>
              <p className="text-xs opacity-60 mt-1">{fmtDateTime(m.created_at)}</p>
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}

function DirectMessageDialog({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState(null);
  const [f, setF] = useState({ subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setQ(""); setResults([]); setSel(null); setF({ subject: "", body: "" }); } }, [open]);
  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return; }
    const id = setTimeout(() => { api.get(`/admin/users?q=${encodeURIComponent(q)}`).then((r) => setResults((r.data || []).slice(0, 8))).catch(() => setResults([])); }, 250);
    return () => clearTimeout(id);
  }, [q, open]);
  const send = async (e) => {
    e.preventDefault();
    if (!sel) return toast.error("Bitte einen Nutzer wählen");
    setBusy(true);
    try { await api.post("/admin/inbox/message", { user_id: sel.id, subject: f.subject, body: f.body }); toast.success(`Nachricht an ${sel.name} gesendet`); onClose(true); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent><DialogHeader><DialogTitle>Nachricht an einzelnen Nutzer</DialogTitle></DialogHeader>
        <form className="space-y-4" onSubmit={send}>
          {sel ? (
            <div className="w-card p-3 flex items-center justify-between gap-2" data-testid="dm-selected">
              <span className="text-sm"><strong>{sel.name}</strong> · {sel.email} <Chip tone="info">{sel.role}</Chip></span>
              <button type="button" className="w-muted underline text-sm" onClick={() => setSel(null)} data-testid="dm-change-user">Ändern</button>
            </div>
          ) : (
            <div>
              <label className="block"><span className="w-label">Nutzer suchen (Name oder E-Mail)</span>
                <input className="w-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="z. B. kunde@…" data-testid="dm-search" autoFocus /></label>
              {results.length > 0 && (
                <div className="mt-2 border border-line rounded-xl divide-y max-h-56 overflow-y-auto" data-testid="dm-results">
                  {results.map((u) => (
                    <button key={u.id} type="button" className="w-full text-left p-3 hover:bg-mint-soft flex items-center justify-between gap-2" onClick={() => setSel(u)} data-testid={`dm-user-${u.id}`}>
                      <span className="text-sm"><strong>{u.name}</strong> · {u.email}</span><Chip tone="info">{u.role}</Chip>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <label className="block"><span className="w-label">Betreff</span><input className="w-input" required minLength={2} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="dm-subject" /></label>
          <label className="block"><span className="w-label">Nachricht</span><textarea className="w-input !h-28 py-3" required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="dm-body" /></label>
          <button className="w-btn w-btn-primary w-full" disabled={busy || !sel} data-testid="dm-send"><Send size={18} aria-hidden="true" /> {busy ? "Sende…" : "Senden"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BroadcastDialog({ open, onClose }) {
  const [f, setF] = useState({ subject: "", body: "", target: "all" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ subject: "", body: "", target: "all" }); }, [open]);
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/admin/inbox/broadcast", f); toast.success(`An ${data.sent} Nutzer gesendet`); onClose(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent><DialogHeader><DialogTitle>Rundnachricht</DialogTitle></DialogHeader>
        <form className="space-y-4" onSubmit={send}>
          <label className="block"><span className="w-label">Empfänger</span>
            <select className="w-input" value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} data-testid="broadcast-target">
              <option value="all">Alle Nutzer</option><option value="customer">Nur Kunden</option><option value="provider">Nur Waschhelden</option>
            </select></label>
          <label className="block"><span className="w-label">Betreff</span><input className="w-input" required minLength={2} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="broadcast-subject" /></label>
          <label className="block"><span className="w-label">Nachricht</span><textarea className="w-input !h-28 py-3" required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="broadcast-body" /></label>
          <button className="w-btn w-btn-primary w-full" disabled={busy} data-testid="broadcast-send"><Megaphone size={18} aria-hidden="true" /> {busy ? "Sende…" : "Jetzt senden"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
